import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { User, Mail, Phone, ExternalLink, Share2, Download, QrCode, MapPin, Star } from 'lucide-react';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaWhatsapp, 
  FaTelegram, FaSnapchat, FaPinterest, FaTiktok, FaDiscord, FaReddit,
  FaGlobe, FaGithub, FaSpotify, FaThreads
} from 'react-icons/fa6';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { copyToClipboard } from '../utils/clipboard';
import { getImageUrl } from '../utils/imageUrl';
import Cosmic4KDVCTemplate from '../components/Cosmic4KDVCTemplate';
import SharePopup from '../components/SharePopup';

// METAVERSE-STYLE PREMIUM TEMPLATES (same as DigitalCard.js)
const TEMPLATES = [
  // ===== 4K DVC COSMIC TEMPLATES (NEW!) =====
  { value: 'cosmic-4k-galaxy', label: '4K Cosmic Galaxy', color: '#00BFFF', bg: '#000820', style: '4k-cosmic', 
    gradient: 'from-blue-600 via-purple-600 to-pink-500', glow: 'cyan', animation: 'cosmic' },
  { value: 'cosmic-4k-nebula', label: '4K Cosmic Nebula', color: '#FF6EC7', bg: '#0a0014', style: '4k-cosmic',
    gradient: 'from-pink-500 via-purple-500 to-blue-600', glow: 'pink', animation: 'cosmic' },
  { value: 'cosmic-4k-aurora', label: '4K Cosmic Aurora', color: '#00FF88', bg: '#000a0a', style: '4k-cosmic',
    gradient: 'from-green-400 via-cyan-500 to-purple-600', glow: 'green', animation: 'cosmic' },
  { value: 'cosmic-4k-sunrise', label: '4K Cosmic Sunrise', color: '#FF8C00', bg: '#1a0500', style: '4k-cosmic',
    gradient: 'from-orange-500 via-red-500 to-purple-600', glow: 'orange', animation: 'cosmic' },
  { value: 'cosmic-4k-midnight', label: '4K Cosmic Midnight', color: '#8B5CF6', bg: '#05001a', style: '4k-cosmic',
    gradient: 'from-violet-600 via-indigo-600 to-blue-600', glow: 'purple', animation: 'cosmic' },
    
  // ===== SARAH JENSEN STYLE - Glass Morphism Business =====
  { value: 'business-navy-gold', label: 'Business Navy Gold', color: '#FFD700', bg: '#0a1628', style: 'business-glass',
    gradient: 'from-blue-900 via-slate-800 to-blue-950', glow: 'gold', animation: 'shimmer' },
  { value: 'business-executive', label: 'Executive Suite', color: '#D4AF37', bg: '#0d1520', style: 'business-glass',
    gradient: 'from-slate-800 via-gray-900 to-slate-950', glow: 'gold', animation: 'shimmer' },
  { value: 'business-modern', label: 'Modern Executive', color: '#F59E0B', bg: '#111827', style: 'business-glass',
    gradient: 'from-gray-800 via-slate-800 to-gray-900', glow: 'amber', animation: 'none' },
  { value: 'corporate-blue', label: 'Corporate Blue', color: '#3B82F6', bg: '#0c1929', style: 'business-glass',
    gradient: 'from-blue-900 via-indigo-900 to-slate-900', glow: 'blue', animation: 'pulse' },
  { value: 'corporate-teal', label: 'Corporate Teal', color: '#14B8A6', bg: '#0d1f1f', style: 'business-glass',
    gradient: 'from-teal-900 via-slate-900 to-teal-950', glow: 'teal', animation: 'none' },
    
  // ===== ALEX RIVERS STYLE - Neon Ring Profile =====
  { value: 'neon-ring-cyan', label: 'Neon Ring Cyan', color: '#00FFFF', bg: '#0a0a1a', style: 'neon-ring',
    gradient: 'from-cyan-500 via-blue-500 to-purple-600', glow: 'cyan', animation: 'pulse' },
  { value: 'neon-ring-purple', label: 'Neon Ring Purple', color: '#A855F7', bg: '#0d0020', style: 'neon-ring',
    gradient: 'from-purple-500 via-pink-500 to-blue-500', glow: 'purple', animation: 'pulse' },
  { value: 'neon-ring-green', label: 'Neon Ring Matrix', color: '#22C55E', bg: '#0a120a', style: 'neon-ring',
    gradient: 'from-green-500 via-emerald-500 to-cyan-500', glow: 'green', animation: 'pulse' },
  { value: 'neon-ring-pink', label: 'Neon Ring Pink', color: '#EC4899', bg: '#1a0a18', style: 'neon-ring',
    gradient: 'from-pink-500 via-rose-500 to-purple-500', glow: 'pink', animation: 'pulse' },
  { value: 'neon-ring-orange', label: 'Neon Ring Fire', color: '#F97316', bg: '#1a0f0a', style: 'neon-ring',
    gradient: 'from-orange-500 via-red-500 to-pink-500', glow: 'orange', animation: 'pulse' },
    
  // ===== MAYA SHARMA STYLE - Creative Studio =====
  { value: 'creative-studio-purple', label: 'Creative Studio', color: '#8B5CF6', bg: '#0f0a2a', style: 'creative-studio',
    gradient: 'from-violet-600 via-purple-700 to-blue-800', glow: 'purple', animation: 'gradient' },
  { value: 'creative-vision-blue', label: 'Creative Vision', color: '#06B6D4', bg: '#0a1520', style: 'creative-studio',
    gradient: 'from-cyan-600 via-blue-700 to-purple-700', glow: 'cyan', animation: 'gradient' },
  { value: 'designer-pro', label: 'Designer Pro', color: '#F472B6', bg: '#1a0a20', style: 'creative-studio',
    gradient: 'from-pink-600 via-purple-600 to-indigo-700', glow: 'pink', animation: 'gradient' },
  { value: 'artist-palette', label: 'Artist Palette', color: '#FBBF24', bg: '#1a150a', style: 'creative-studio',
    gradient: 'from-amber-500 via-orange-600 to-red-600', glow: 'amber', animation: 'shimmer' },
  { value: 'tech-innovator', label: 'Tech Innovator', color: '#10B981', bg: '#0a1a15', style: 'creative-studio',
    gradient: 'from-emerald-600 via-teal-600 to-cyan-600', glow: 'green', animation: 'pulse' },
    
  // ===== SARAH J. CHEN STYLE - Split Layout Business =====
  { value: 'split-dark-purple', label: 'Split Dark Purple', color: '#A78BFA', bg: '#0f0f1a', style: 'split-layout',
    gradient: 'from-violet-900 via-purple-900 to-slate-900', glow: 'purple', animation: 'none' },
  { value: 'split-professional', label: 'Split Professional', color: '#60A5FA', bg: '#0a1020', style: 'split-layout',
    gradient: 'from-blue-900 via-slate-900 to-gray-900', glow: 'blue', animation: 'none' },
  { value: 'split-elegant', label: 'Split Elegant', color: '#F9A8D4', bg: '#1a0a15', style: 'split-layout',
    gradient: 'from-pink-900 via-slate-900 to-purple-950', glow: 'pink', animation: 'shimmer' },
  { value: 'split-tech', label: 'Split Tech', color: '#34D399', bg: '#0a1a18', style: 'split-layout',
    gradient: 'from-emerald-900 via-slate-900 to-teal-950', glow: 'green', animation: 'pulse' },
  { value: 'split-minimal', label: 'Split Minimal', color: '#E5E7EB', bg: '#111111', style: 'split-layout',
    gradient: 'from-gray-900 via-slate-900 to-zinc-900', glow: 'white', animation: 'none' },
    
  // ===== 4K QUALITY TEMPLATES =====
  { value: '4k-tech-blue', label: '4K Tech Blue', color: '#38BDF8', bg: '#0c1929', style: '4k-quality',
    gradient: 'from-sky-600 via-blue-700 to-indigo-800', glow: 'cyan', animation: 'pulse' },
  { value: '4k-tech-green', label: '4K Tech Green', color: '#4ADE80', bg: '#0a1a12', style: '4k-quality',
    gradient: 'from-green-600 via-emerald-700 to-teal-800', glow: 'green', animation: 'pulse' },
  { value: '4k-premium-gold', label: '4K Premium Gold', color: '#FCD34D', bg: '#1a1508', style: '4k-quality',
    gradient: 'from-amber-600 via-yellow-600 to-orange-700', glow: 'gold', animation: 'shimmer' },
  { value: '4k-elegant-rose', label: '4K Elegant Rose', color: '#FB7185', bg: '#1a0a10', style: '4k-quality',
    gradient: 'from-rose-600 via-pink-700 to-purple-800', glow: 'pink', animation: 'shimmer' },
  { value: '4k-ocean-deep', label: '4K Ocean Deep', color: '#22D3EE', bg: '#0a1520', style: '4k-quality',
    gradient: 'from-cyan-600 via-blue-700 to-indigo-900', glow: 'cyan', animation: 'wave' },
    
  // ===== SOCIAL GRID TEMPLATES (Like Alex Rivers) =====
  { value: 'social-grid-dark', label: 'Social Grid Dark', color: '#818CF8', bg: '#0a0a15', style: 'social-grid',
    gradient: 'from-indigo-900 via-purple-900 to-slate-900', glow: 'purple', animation: 'none' },
  { value: 'social-grid-neon', label: 'Social Grid Neon', color: '#22D3EE', bg: '#0a1018', style: 'social-grid',
    gradient: 'from-cyan-900 via-blue-900 to-purple-900', glow: 'cyan', animation: 'pulse' },
  { value: 'social-grid-gradient', label: 'Social Grid Gradient', color: '#F472B6', bg: '#0f0a18', style: 'social-grid',
    gradient: 'from-pink-900 via-purple-900 to-indigo-900', glow: 'pink', animation: 'gradient' },
  { value: 'social-grid-minimal', label: 'Social Grid Minimal', color: '#94A3B8', bg: '#0f0f0f', style: 'social-grid',
    gradient: 'from-slate-900 via-gray-900 to-zinc-900', glow: 'white', animation: 'none' },
  { value: 'social-grid-vibrant', label: 'Social Grid Vibrant', color: '#A3E635', bg: '#0a1508', style: 'social-grid',
    gradient: 'from-lime-900 via-green-900 to-emerald-900', glow: 'green', animation: 'pulse' },
    
  // ===== NEW CYBERCITY METAVERSE TEMPLATES =====
  { value: 'cybercity-neon', label: 'Cybercity Neon', color: '#00FFFF', bg: '#0a0a1a', style: 'cybercity', 
    gradient: 'from-cyan-500 via-purple-500 to-pink-500', glow: 'cyan', animation: 'pulse',
    bgImage: 'cybercity' },
  { value: 'cybercity-purple', label: 'Cybercity Purple', color: '#A855F7', bg: '#0d0020', style: 'cybercity',
    gradient: 'from-purple-600 via-blue-600 to-cyan-500', glow: 'purple', animation: 'pulse',
    bgImage: 'cybercity-purple' },
  { value: 'cybercity-rainbow', label: 'Cybercity Rainbow', color: '#FF6EC7', bg: '#0a0014', style: 'cybercity',
    gradient: 'from-pink-500 via-orange-500 to-green-500', glow: 'pink', animation: 'gradient',
    bgImage: 'cybercity-rainbow' },
  { value: 'cybercity-green', label: 'Cybercity Matrix', color: '#00FF88', bg: '#000a0a', style: 'cybercity',
    gradient: 'from-green-500 via-cyan-500 to-blue-500', glow: 'green', animation: 'pulse',
    bgImage: 'cybercity-green' },
  { value: 'cybercity-gold', label: 'Cybercity Gold', color: '#FFD700', bg: '#0a0808', style: 'cybercity',
    gradient: 'from-amber-500 via-orange-500 to-red-500', glow: 'gold', animation: 'shimmer',
    bgImage: 'cybercity-gold' },
    
  // ===== HYPER FUTURISTIC TEMPLATES =====
  { value: 'metaverse-neon', label: 'Metaverse Neon', color: '#00FFFF', bg: '#0a0014', style: 'metaverse', 
    gradient: 'from-violet-600 via-fuchsia-600 to-cyan-500', glow: 'cyan', animation: 'pulse' },
  { value: 'cyber-punk', label: 'Cyber Punk', color: '#FF00FF', bg: '#0d0015', style: 'cyberpunk',
    gradient: 'from-pink-600 via-purple-600 to-blue-600', glow: 'pink', animation: 'glitch' },
  { value: 'hologram', label: 'Hologram 3D', color: '#00FF88', bg: '#000a0a', style: 'hologram',
    gradient: 'from-emerald-400 via-cyan-500 to-blue-500', glow: 'green', animation: 'float' },
  { value: 'matrix-code', label: 'Matrix Code', color: '#00FF00', bg: '#000800', style: 'matrix',
    gradient: 'from-green-500 to-emerald-700', glow: 'green', animation: 'rain' },
  { value: 'quantum', label: 'Quantum Flux', color: '#8B5CF6', bg: '#0a0020', style: 'quantum',
    gradient: 'from-violet-500 via-purple-500 to-pink-500', glow: 'purple', animation: 'wave' },
  { value: 'neon-grid', label: 'Neon Grid', color: '#FF3366', bg: '#080818', style: 'metaverse',
    gradient: 'from-rose-500 via-pink-500 to-violet-500', glow: 'pink', animation: 'pulse' },
  { value: 'synthwave', label: 'Synthwave', color: '#FF6EC7', bg: '#1a0a2e', style: 'metaverse',
    gradient: 'from-purple-500 via-pink-400 to-orange-400', glow: 'pink', animation: 'gradient' },
  { value: 'virtual-reality', label: 'Virtual Reality', color: '#00E5FF', bg: '#001428', style: 'metaverse',
    gradient: 'from-blue-400 via-cyan-400 to-teal-400', glow: 'cyan', animation: 'pulse' },
    
  // ===== PREMIUM LUXURY TEMPLATES =====
  { value: 'gold-luxury', label: 'Gold Luxury', color: '#FFD700', bg: '#1a1000', style: 'luxury',
    gradient: 'from-amber-500 via-yellow-400 to-amber-600', glow: 'gold', animation: 'shimmer' },
  { value: 'diamond', label: 'Diamond Elite', color: '#B9F2FF', bg: '#0a1520', style: 'diamond',
    gradient: 'from-cyan-200 via-white to-cyan-300', glow: 'white', animation: 'sparkle' },
  { value: 'carbon-fiber', label: 'Carbon Fiber', color: '#FFFFFF', bg: '#0a0a0a', style: 'carbon',
    gradient: 'from-gray-800 via-gray-900 to-black', glow: 'white', animation: 'none' },
  { value: 'platinum', label: 'Platinum Premium', color: '#E5E4E2', bg: '#15151A', style: 'luxury',
    gradient: 'from-gray-300 via-white to-gray-400', glow: 'white', animation: 'shimmer' },
  { value: 'rose-gold', label: 'Rose Gold', color: '#E8B4B8', bg: '#1a1215', style: 'luxury',
    gradient: 'from-rose-300 via-pink-200 to-amber-200', glow: 'pink', animation: 'shimmer' },
  { value: 'emerald', label: 'Emerald Royal', color: '#50C878', bg: '#0a1a10', style: 'luxury',
    gradient: 'from-emerald-400 via-green-500 to-teal-600', glow: 'green', animation: 'none' },
  { value: 'sapphire', label: 'Sapphire Blue', color: '#0F52BA', bg: '#0a0a20', style: 'luxury',
    gradient: 'from-blue-500 via-indigo-600 to-violet-700', glow: 'blue', animation: 'shimmer' },
  { value: 'ruby', label: 'Ruby Red', color: '#E0115F', bg: '#1a0a0a', style: 'luxury',
    gradient: 'from-red-500 via-rose-600 to-pink-700', glow: 'red', animation: 'shimmer' },
  { value: 'obsidian', label: 'Obsidian Black', color: '#36454F', bg: '#050505', style: 'luxury',
    gradient: 'from-gray-900 via-slate-900 to-black', glow: 'gray', animation: 'none' },
  
  // ===== NATURE & COSMIC TEMPLATES =====
  { value: 'aurora', label: 'Aurora Borealis', color: '#7CFF7C', bg: '#001020', style: 'aurora',
    gradient: 'from-green-400 via-cyan-500 to-purple-600', glow: 'cyan', animation: 'aurora' },
  { value: 'galaxy', label: 'Galaxy Stars', color: '#E0B0FF', bg: '#050510', style: 'galaxy',
    gradient: 'from-purple-600 via-pink-500 to-orange-400', glow: 'purple', animation: 'twinkle' },
  { value: 'sunset-vibes', label: 'Sunset Vibes', color: '#FF6B6B', bg: '#1a0a10', style: 'sunset',
    gradient: 'from-orange-500 via-rose-500 to-purple-600', glow: 'orange', animation: 'gradient' },
  { value: 'nebula', label: 'Cosmic Nebula', color: '#C084FC', bg: '#0D0418', style: 'galaxy',
    gradient: 'from-fuchsia-500 via-purple-600 to-blue-700', glow: 'purple', animation: 'twinkle' },
  { value: 'northern-lights', label: 'Northern Lights', color: '#4ADE80', bg: '#061010', style: 'aurora',
    gradient: 'from-teal-400 via-emerald-400 to-cyan-500', glow: 'green', animation: 'aurora' },
  { value: 'fire-storm', label: 'Fire Storm', color: '#FF4500', bg: '#1A0800', style: 'sunset',
    gradient: 'from-red-500 via-orange-500 to-amber-500', glow: 'orange', animation: 'gradient' },
  { value: 'ocean-wave', label: 'Ocean Wave', color: '#0EA5E9', bg: '#0a1520', style: 'ocean',
    gradient: 'from-blue-600 via-cyan-600 to-teal-600', glow: 'cyan', animation: 'wave' },
  { value: 'forest-mist', label: 'Forest Mist', color: '#22C55E', bg: '#0a1a0a', style: 'aurora',
    gradient: 'from-green-600 via-emerald-600 to-teal-600', glow: 'green', animation: 'none' },
  { value: 'starfield', label: 'Starfield', color: '#FBBF24', bg: '#050510', style: 'galaxy',
    gradient: 'from-amber-500 via-yellow-400 to-orange-500', glow: 'gold', animation: 'twinkle' },
  
  // ===== CLASSIC PREMIUM TEMPLATES =====
  { value: 'default', label: 'Classic Gold', color: '#D4AF37', bg: '#0a0a0a', style: 'classic',
    gradient: 'from-amber-600 to-yellow-500', glow: 'gold', animation: 'none' },
  { value: 'tech-cyan', label: 'Tech Cyan', color: '#00D9FF', bg: '#0a192f', style: 'tech',
    gradient: 'from-cyan-500 to-blue-600', glow: 'cyan', animation: 'pulse' },
  { value: 'ocean', label: 'Deep Ocean', color: '#06B6D4', bg: '#0c4a6e', style: 'ocean',
    gradient: 'from-blue-600 via-cyan-600 to-teal-500', glow: 'blue', animation: 'wave' },
  { value: 'royal', label: 'Royal Purple', color: '#A855F7', bg: '#1c1017', style: 'royal',
    gradient: 'from-purple-600 via-violet-600 to-indigo-600', glow: 'purple', animation: 'none' },
  { value: 'midnight', label: 'Midnight Blue', color: '#3B82F6', bg: '#0A1628', style: 'classic',
    gradient: 'from-blue-500 via-indigo-500 to-violet-500', glow: 'blue', animation: 'none' },
  { value: 'forest', label: 'Forest Green', color: '#22C55E', bg: '#0A1A0A', style: 'classic',
    gradient: 'from-green-500 via-emerald-500 to-teal-500', glow: 'green', animation: 'none' },
  
  // ===== LEGACY TEMPLATES =====
  { value: 'tech-gold', label: 'Tech Gold', color: '#D4AF37', bg: '#0a0a0a', style: 'luxury',
    gradient: 'from-amber-500 via-yellow-500 to-amber-600', glow: 'gold', animation: 'shimmer' },
  { value: 'modern', label: 'Modern', color: '#3B82F6', bg: '#0a192f', style: 'tech',
    gradient: 'from-blue-500 to-indigo-600', glow: 'blue', animation: 'pulse' },
  { value: 'elegant', label: 'Elegant', color: '#A855F7', bg: '#1a1025', style: 'luxury',
    gradient: 'from-purple-500 to-pink-500', glow: 'purple', animation: 'shimmer' },
  { value: 'minimal', label: 'Minimal', color: '#FFFFFF', bg: '#111111', style: 'classic',
    gradient: 'from-gray-400 to-gray-600', glow: 'white', animation: 'none' },
  { value: 'gradient', label: 'Gradient', color: '#EC4899', bg: '#1a0a20', style: 'metaverse',
    gradient: 'from-pink-500 via-purple-500 to-blue-500', glow: 'pink', animation: 'gradient' },
];

const SOCIAL_ICONS = {
  facebook: { icon: FaFacebook, color: '#1877F2' },
  twitter: { icon: FaTwitter, color: '#1DA1F2' },
  instagram: { icon: FaInstagram, color: '#E4405F' },
  linkedin: { icon: FaLinkedin, color: '#0A66C2' },
  youtube: { icon: FaYoutube, color: '#FF0000' },
  whatsapp: { icon: FaWhatsapp, color: '#25D366' },
  telegram: { icon: FaTelegram, color: '#0088cc' },
  snapchat: { icon: FaSnapchat, color: '#FFFC00' },
  tiktok: { icon: FaTiktok, color: '#000000' },
  pinterest: { icon: FaPinterest, color: '#E60023' },
  discord: { icon: FaDiscord, color: '#5865F2' },
  reddit: { icon: FaReddit, color: '#FF4500' },
  threads: { icon: FaThreads, color: '#000000' },
  spotify: { icon: FaSpotify, color: '#1DB954' },
  github: { icon: FaGithub, color: '#181717' },
  website: { icon: FaGlobe, color: '#6366F1' },
};

// CSS Animations for Metaverse Templates
const MetaverseStyles = () => (
  <style>{`
    @keyframes matrixFall {
      from { transform: translateY(-100%); }
      to { transform: translateY(100vh); }
    }
    @keyframes hologramScan {
      0%, 100% { opacity: 0.2; transform: translateY(0); }
      50% { opacity: 0.6; transform: translateY(10px); }
    }
    @keyframes float {
      0%, 100% { transform: translate(-50%, -50%) translateY(0); }
      50% { transform: translate(-50%, -50%) translateY(-10px); }
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes floatIcon {
      0%, 100% { 
        transform: translateY(0) scale(1);
        filter: brightness(1);
      }
      50% { 
        transform: translateY(-12px) scale(1.05);
        filter: brightness(1.2);
      }
    }
    @keyframes float3D {
      0%, 100% { 
        transform: translateY(0) rotateY(0deg) scale(1);
      }
      25% { 
        transform: translateY(-15px) rotateY(10deg) scale(1.1);
      }
      50% { 
        transform: translateY(-5px) rotateY(0deg) scale(1.05);
      }
      75% { 
        transform: translateY(-20px) rotateY(-10deg) scale(1.15);
      }
    }
    @keyframes orbitSlow {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes orbitReverse {
      from { transform: rotate(360deg); }
      to { transform: rotate(0deg); }
    }
    @keyframes particleTrail {
      0% { 
        transform: translate(0, 0) scale(1);
        opacity: 0.8;
      }
      100% { 
        transform: translate(-20px, 20px) scale(0);
        opacity: 0;
      }
    }
    @keyframes morphShape {
      0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
      25% { border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%; }
      50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
      75% { border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%; }
    }
    @keyframes pulseGlow {
      0%, 100% { 
        transform: scale(1);
        opacity: 1;
      }
      50% { 
        transform: scale(1.05);
        opacity: 0.8;
      }
    }
    @keyframes orbitParticle {
      from { transform: rotate(0deg) translateX(85px) translateY(-50%); }
      to { transform: rotate(360deg) translateX(85px) translateY(-50%); }
    }
    @keyframes twinkle {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }
    @keyframes textGlow {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.85; }
    }
    @keyframes shine {
      0% { background-position: -200% -200%; }
      100% { background-position: 200% 200%; }
    }
    @keyframes shimmer {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    @keyframes diamondSparkle {
      0%, 100% { opacity: 0.3; transform: scale(1) rotate(0deg); }
      50% { opacity: 0.8; transform: scale(1.2) rotate(180deg); }
    }
    @keyframes luxuryFloat {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      50% { transform: translateY(-5px) rotate(2deg); }
    }
    @keyframes glowPulse {
      0%, 100% { box-shadow: 0 0 20px currentColor, 0 0 40px currentColor; }
      50% { box-shadow: 0 0 40px currentColor, 0 0 80px currentColor; }
    }
    @keyframes lightStreak {
      0% { transform: translateX(-100%) rotate(45deg); opacity: 0; }
      50% { opacity: 0.6; }
      100% { transform: translateX(200%) rotate(45deg); opacity: 0; }
    }
    @keyframes neonFlicker {
      0%, 100% { opacity: 1; }
      92% { opacity: 1; }
      93% { opacity: 0.3; }
      94% { opacity: 1; }
      96% { opacity: 0.4; }
      97% { opacity: 1; }
    }
    @keyframes holographicShine {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes iconBounce {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-8px) scale(1.05); }
    }
  `}</style>
);

const PublicCard = () => {
  const { username } = useParams();
  const cardRef = useRef(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchCard();
  }, [username]);

  const fetchCard = async () => {
    try {
      const res = await api.get(`/dvc/by-username/${username}`);
      setCard(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Card not found');
    } finally {
      setLoading(false);
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: card?.background_color || '#0a0a0a',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `${username}-card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to download:', error);
    } finally {
      setDownloading(false);
    }
  };

  const getSocialIcon = (platform) => {
    return SOCIAL_ICONS[platform]?.icon || FaGlobe;
  };

  const getSocialColor = (platform) => {
    return SOCIAL_ICONS[platform]?.color || '#6366F1';
  };

  const getCurrentTemplate = () => {
    const templateValue = card?.template || 'default';
    return TEMPLATES.find(t => t.value === templateValue) || TEMPLATES.find(t => t.value === 'default');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <User className="w-20 h-20 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Card Not Found</h1>
          <p className="text-gray-400">{error}</p>
          <a href="/" className="mt-4 inline-block text-primary hover:underline">
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  const currentTemplate = getCurrentTemplate();
  const primaryColor = card?.primary_color || currentTemplate?.color || '#D4AF37';
  const bgColor = card?.background_color || currentTemplate?.bg || '#0a0a0a';
  const templateStyle = currentTemplate?.style || 'classic';
  const cardUrl = window.location.href;

  // Determine template type
  const is4KCosmic = templateStyle === '4k-cosmic';
  const isMetaverse = ['metaverse', 'cyberpunk', 'hologram', 'matrix', 'quantum', 'aurora', 'galaxy', 'sunset'].includes(templateStyle);
  const isLuxury = ['luxury', 'diamond', 'carbon'].includes(templateStyle);
  const isCybercity = templateStyle === 'cybercity';

  // ===== 4K COSMIC TEMPLATE RENDERING =====
  const render4KCosmicCard = () => {
    // Map template value to variant
    const variantMap = {
      'cosmic-4k-galaxy': 'galaxy',
      'cosmic-4k-nebula': 'nebula',
      'cosmic-4k-aurora': 'aurora',
      'cosmic-4k-sunrise': 'sunrise',
      'cosmic-4k-midnight': 'midnight',
    };
    const variant = variantMap[card?.template] || 'galaxy';
    
    // Build card data for the component
    const cardData = {
      display_name: card?.display_name || username,
      tagline: card?.tagline,
      bio: card?.bio,
      profile_image: card?.profile_image ? getImageUrl(card.profile_image) : null,
      social_links: card?.social_links || [],
      show_phone: card?.show_phone || card?.show_mobile,
      phone: card?.mobile_number || card?.phone,
      address: card?.tagline || 'Digital Entrepreneur',
    };
    
    return (
      <div ref={cardRef}>
        <Cosmic4KDVCTemplate 
          card={cardData}
          variant={variant}
        />
      </div>
    );
  };

  // ===== CYBERCITY TEMPLATE RENDERING - Mobile Responsive =====
  const renderCybercityCard = () => {
    const socialLinks = card?.social_links || [];
    
    // Mobile-friendly layout - show social icons in a grid instead of floating
    const isMobileView = typeof window !== 'undefined' && window.innerWidth < 480;

    return (
      <div 
        ref={cardRef}
        className="rounded-3xl overflow-hidden shadow-2xl relative"
        style={{ 
          backgroundColor: currentTemplate?.bg || '#0a0a1a',
          minHeight: 'auto'
        }}
      >
        {/* ===== CYBERCITY BACKGROUND ===== */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Base gradient - dark city sky */}
          <div 
            className="absolute inset-0"
            style={{
              background: currentTemplate?.value?.includes('purple') 
                ? 'linear-gradient(180deg, #1a0a30 0%, #0d0020 50%, #0a0a1a 100%)'
                : currentTemplate?.value?.includes('rainbow')
                ? 'linear-gradient(180deg, #1a0a20 0%, #0a1020 50%, #0a0a14 100%)'
                : 'linear-gradient(180deg, #0a1020 0%, #050810 50%, #0a0a1a 100%)'
            }}
          />
          
          {/* City skyline silhouette */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-24 sm:h-48 opacity-40"
            style={{
              background: `linear-gradient(0deg, ${currentTemplate?.color}20 0%, transparent 100%)`
            }}
          />
          
          {/* Neon light streaks - fewer on mobile */}
          <div className="absolute inset-0 overflow-hidden hidden sm:block">
            {[...Array(6)].map((_, i) => (
              <div
                key={`streak-${i}`}
                className="absolute h-1 opacity-40"
                style={{
                  width: '150px',
                  left: `${i * 20 - 10}%`,
                  top: `${25 + i * 12}%`,
                  background: `linear-gradient(90deg, transparent, ${
                    i % 3 === 0 ? '#00FFFF' : i % 3 === 1 ? '#FF00FF' : '#00FF88'
                  }, transparent)`,
                  transform: `rotate(${-30 + i * 5}deg)`,
                  animation: `lightStreak ${4 + i * 0.5}s linear infinite`,
                  animationDelay: `${i * 0.8}s`
                }}
              />
            ))}
          </div>
          
          {/* Glowing orbs - smaller on mobile */}
          <div 
            className="absolute w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] rounded-full blur-[60px] sm:blur-[100px] animate-pulse"
            style={{ 
              background: `radial-gradient(circle, ${currentTemplate?.color}30 0%, transparent 70%)`,
              top: '-80px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />
          
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `linear-gradient(${currentTemplate?.color}30 1px, transparent 1px), linear-gradient(90deg, ${currentTemplate?.color}30 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>

        {/* ===== CONTENT ===== */}
        <div className="relative z-10 px-4 sm:px-6 py-6 sm:py-8">
          
          {/* ===== CENTRAL PROFILE FRAME - Always visible ===== */}
          <div className="flex justify-center mb-6">
            <div className="relative w-32 sm:w-44 h-40 sm:h-52">
              {/* Holographic outer frame */}
              <div 
                className="absolute -inset-2 sm:-inset-4 rounded-xl sm:rounded-2xl"
                style={{
                  background: `linear-gradient(135deg, ${currentTemplate?.color}30, transparent, ${currentTemplate?.color}20)`,
                  border: `2px solid ${currentTemplate?.color}40`,
                  boxShadow: `
                    0 0 20px ${currentTemplate?.color}30,
                    0 0 40px ${currentTemplate?.color}15
                  `,
                  animation: 'neonFlicker 8s ease-in-out infinite'
                }}
              />
              
              {/* Inner glowing border */}
              <div 
                className="absolute -inset-0.5 sm:-inset-1 rounded-lg sm:rounded-xl"
                style={{
                  background: `linear-gradient(180deg, ${currentTemplate?.color}60, ${currentTemplate?.color}20)`,
                  padding: '2px'
                }}
              >
                <div 
                  className="w-full h-full rounded-lg sm:rounded-xl"
                  style={{ backgroundColor: currentTemplate?.bg }}
                />
              </div>
              
              {/* Profile image container */}
              <div 
                className="absolute inset-0 rounded-lg sm:rounded-xl overflow-hidden flex items-center justify-center"
                style={{
                  backgroundColor: currentTemplate?.bg,
                  boxShadow: `inset 0 0 20px ${currentTemplate?.color}20`
                }}
              >
                {card?.profile_image ? (
                  <img 
                    src={getImageUrl(card.profile_image)} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-14 sm:w-20 h-14 sm:h-20" style={{ color: currentTemplate?.color }} />
                )}
                
                {/* Holographic overlay */}
                <div 
                  className="absolute inset-0 pointer-events-none opacity-30"
                  style={{
                    background: `linear-gradient(180deg, transparent 60%, ${currentTemplate?.color}40 100%)`
                  }}
                />
              </div>
              
              {/* Corner accents - smaller on mobile */}
              {['-top-1 -left-1 sm:-top-2 sm:-left-2', '-top-1 -right-1 sm:-top-2 sm:-right-2', '-bottom-1 -left-1 sm:-bottom-2 sm:-left-2', '-bottom-1 -right-1 sm:-bottom-2 sm:-right-2'].map((pos, i) => (
                <div 
                  key={i}
                  className={`absolute ${pos} w-2 sm:w-3 h-2 sm:h-3`}
                  style={{
                    backgroundColor: currentTemplate?.color,
                    boxShadow: `0 0 8px ${currentTemplate?.color}`,
                    clipPath: i < 2 ? 'polygon(0 0, 100% 0, 0 100%)' : 'polygon(100% 0, 100% 100%, 0 100%)',
                    transform: `rotate(${i * 90}deg)`
                  }}
                />
              ))}
            </div>
          </div>

          {/* ===== SOCIAL ICONS GRID - Mobile Friendly ===== */}
          {socialLinks.length > 0 && (
            <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 gap-2 sm:gap-3 mb-6">
              {socialLinks.slice(0, 12).map((link, idx) => {
                const Icon = getSocialIcon(link.platform);
                const socialColor = getSocialColor(link.platform);
                
                return (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 p-2 sm:p-3 rounded-xl transition-all hover:scale-105"
                    style={{
                      background: `linear-gradient(135deg, ${socialColor}20, ${socialColor}05)`,
                      border: `1px solid ${socialColor}40`,
                      boxShadow: `0 0 15px ${socialColor}20`
                    }}
                  >
                    <div 
                      className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center"
                      style={{
                        background: `${socialColor}30`,
                        boxShadow: `0 0 10px ${socialColor}30`
                      }}
                    >
                      <Icon 
                        className="w-4 sm:w-5 h-4 sm:h-5" 
                        style={{ 
                          color: socialColor,
                          filter: `drop-shadow(0 0 4px ${socialColor})`
                        }} 
                      />
                    </div>
                    <span className="text-[9px] sm:text-[10px] text-gray-400 truncate w-full text-center capitalize">
                      {link.platform}
                    </span>
                  </a>
                );
              })}
            </div>
          )}

          {/* ===== CONTACT INFO BAR - Responsive ===== */}
          <div 
            className="flex flex-col xs:flex-row items-center justify-center gap-3 xs:gap-4 sm:gap-6 py-3 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl mx-auto max-w-md"
            style={{
              background: `linear-gradient(90deg, ${currentTemplate?.color}15, ${currentTemplate?.color}08, ${currentTemplate?.color}15)`,
              border: `1px solid ${currentTemplate?.color}30`
            }}
          >
            {/* Phone */}
            {(card?.show_mobile && card?.mobile_number) || (card?.show_phone && card?.phone) ? (
              <a 
                href={`tel:${(card?.mobile_number || card?.phone || '').replace(/\s/g, '')}`}
                className="flex items-center gap-2 group"
              >
                <div 
                  className="w-7 sm:w-8 h-7 sm:h-8 rounded-full flex items-center justify-center"
                  style={{ 
                    backgroundColor: `${currentTemplate?.color}30`,
                    boxShadow: `0 0 10px ${currentTemplate?.color}40`
                  }}
                >
                  <Phone className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: currentTemplate?.color }} />
                </div>
                <div className="text-left">
                  <p className="text-[8px] sm:text-[10px] text-gray-500 uppercase tracking-wider">CALL</p>
                  <p 
                    className="text-xs sm:text-sm font-bold"
                    style={{ color: currentTemplate?.color }}
                  >
                    {card?.mobile_number || card?.phone}
                  </p>
                </div>
              </a>
            ) : null}
            
            {/* Divider - Hidden on very small screens */}
            <div className="hidden xs:block w-px h-8 sm:h-10 bg-white/20" />
            
            {/* Address/Location */}
            <div className="flex items-center gap-2">
              <div 
                className="w-7 sm:w-8 h-7 sm:h-8 rounded-full flex items-center justify-center"
                style={{ 
                  backgroundColor: `${currentTemplate?.color}30`,
                  boxShadow: `0 0 10px ${currentTemplate?.color}40`
                }}
              >
                <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" viewBox="0 0 24 24" stroke={currentTemplate?.color}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-[8px] sm:text-[10px] text-gray-500 uppercase tracking-wider">ADDRESS</p>
                <p 
                  className="text-xs sm:text-sm font-bold max-w-[120px] sm:max-w-[150px] truncate"
                  style={{ color: currentTemplate?.color }}
                >
                  {card?.tagline || 'Digital Entrepreneur'}
                </p>
              </div>
            </div>
          </div>

          {/* Name - Responsive */}
          <h3 
            className="text-xl sm:text-3xl font-black text-center mt-4 sm:mt-6 tracking-wide"
            style={{ 
              color: currentTemplate?.color,
              textShadow: `0 0 20px ${currentTemplate?.color}80, 0 0 40px ${currentTemplate?.color}40`
            }}
          >
            {card?.display_name || username}
          </h3>

          {/* Bio */}
          {card?.bio && (
            <p className="text-center text-gray-400 text-xs sm:text-sm mt-2 max-w-xs mx-auto line-clamp-3 px-2">
              {card.bio}
            </p>
          )}

          {/* Custom Links - Responsive */}
          {card?.custom_links?.length > 0 && (
            <div className="mt-4 sm:mt-6 space-y-2 max-w-xs mx-auto px-2">
              {card.custom_links.slice(0, 4).map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-center font-bold text-sm transition-all hover:scale-[1.02]"
                  style={{ 
                    background: `linear-gradient(135deg, ${currentTemplate?.color}20, ${currentTemplate?.color}05)`,
                    border: `1px solid ${currentTemplate?.color}40`,
                    color: currentTemplate?.color,
                    boxShadow: `0 0 15px ${currentTemplate?.color}15`
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          )}

          {/* Referral Code - Responsive */}
          {card?.referral_code && (
            <div className="mt-6 sm:mt-8 text-center px-2">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-2 tracking-wider">JOIN MY NETWORK</p>
              <div 
                className="inline-block px-5 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl"
                style={{
                  background: `linear-gradient(135deg, ${currentTemplate?.color}15, ${currentTemplate?.color}05)`,
                  border: `2px solid ${currentTemplate?.color}40`,
                  boxShadow: `0 0 20px ${currentTemplate?.color}20`
                }}
              >
                <p 
                  className="font-mono text-lg sm:text-2xl font-black"
                  style={{ 
                    color: currentTemplate?.color,
                    textShadow: `0 0 15px ${currentTemplate?.color}60`
                  }}
                >
                  {card.referral_code}
                </p>
              </div>
              <a 
                href={`/register?ref=${card.username}`}
                className="block mt-2 sm:mt-3 text-xs sm:text-sm font-bold transition-all hover:scale-105 mx-auto w-fit px-4 sm:px-6 py-1.5 sm:py-2 rounded-full"
                style={{ 
                  backgroundColor: currentTemplate?.color, 
                  color: currentTemplate?.bg
                }}
              >
                Join My Team
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  // METAVERSE TEMPLATE RENDERING
  const renderMetaverseCard = () => (
    <div 
      ref={cardRef}
      className="rounded-3xl overflow-hidden shadow-2xl relative"
      style={{ 
        backgroundColor: currentTemplate?.bg || '#0a0014',
        minHeight: '550px'
      }}
    >
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `linear-gradient(${currentTemplate?.color}20 1px, transparent 1px), linear-gradient(90deg, ${currentTemplate?.color}20 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
        
        {/* Glowing orbs */}
        <div 
          className="absolute w-[400px] h-[400px] rounded-full blur-[100px] animate-pulse"
          style={{ 
            background: `radial-gradient(circle, ${currentTemplate?.color}40 0%, transparent 70%)`,
            top: '-100px',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        />
        <div 
          className="absolute w-[300px] h-[300px] rounded-full blur-[80px]"
          style={{ 
            background: `radial-gradient(circle, ${currentTemplate?.color}30 0%, transparent 70%)`,
            bottom: '-50px',
            right: '-50px',
            animation: 'pulse 3s ease-in-out infinite alternate'
          }}
        />
        
        {/* Scan lines for cyberpunk */}
        {templateStyle === 'cyberpunk' && (
          <div className="absolute inset-0 pointer-events-none opacity-30" style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,255,0.1) 2px, rgba(255,0,255,0.1) 4px)'
          }} />
        )}
        
        {/* Matrix rain effect */}
        {templateStyle === 'matrix' && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute text-green-500 text-xs font-mono whitespace-pre"
                style={{
                  left: `${i * 7}%`,
                  animation: `matrixFall ${2 + Math.random() * 3}s linear infinite`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                {Array.from({ length: 15 }, () => String.fromCharCode(0x30A0 + Math.random() * 96)).join('\n')}
              </div>
            ))}
          </div>
        )}
        
        {/* Hologram lines */}
        {templateStyle === 'hologram' && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"
                style={{
                  top: `${20 + i * 15}%`,
                  animation: `hologramScan ${3 + i * 0.5}s ease-in-out infinite`,
                  opacity: 0.4
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 px-8 py-10 text-center">
        {/* STUNNING PROFILE FRAME - Diamond/Hexagon Style */}
        <div className="relative w-52 h-52 mx-auto mb-8">
          {/* Multiple rotating rings */}
          <div 
            className="absolute inset-0"
            style={{ 
              background: `conic-gradient(from 0deg, ${currentTemplate?.color}00, ${currentTemplate?.color}60, ${currentTemplate?.color}00)`,
              borderRadius: '50%',
              animation: 'spin 8s linear infinite'
            }}
          />
          <div 
            className="absolute inset-2"
            style={{ 
              background: `conic-gradient(from 180deg, ${currentTemplate?.color}00, ${currentTemplate?.color}40, ${currentTemplate?.color}00)`,
              borderRadius: '50%',
              animation: 'spin 6s linear infinite reverse'
            }}
          />
          
          {/* Diamond shape frame */}
          <div 
            className="absolute inset-4"
            style={{
              background: `linear-gradient(135deg, ${currentTemplate?.color}20, transparent, ${currentTemplate?.color}20)`,
              borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
              border: `2px solid ${currentTemplate?.color}50`,
              boxShadow: `0 0 40px ${currentTemplate?.color}30, inset 0 0 40px ${currentTemplate?.color}10`,
              animation: 'morphShape 8s ease-in-out infinite'
            }}
          />
          
          {/* Pulsing glow ring */}
          <div 
            className="absolute inset-6 rounded-full"
            style={{ 
              border: `3px solid ${currentTemplate?.color}`,
              boxShadow: `0 0 20px ${currentTemplate?.color}, 0 0 40px ${currentTemplate?.color}50, 0 0 60px ${currentTemplate?.color}30`,
              animation: 'pulseGlow 2s ease-in-out infinite'
            }}
          />
          
          {/* Inner hexagon */}
          <div 
            className="absolute inset-8 overflow-hidden flex items-center justify-center"
            style={{ 
              backgroundColor: currentTemplate?.bg,
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              boxShadow: `inset 0 0 30px ${currentTemplate?.color}20`
            }}
          >
            {card?.profile_image ? (
              <img src={getImageUrl(card.profile_image)} alt="Profile" className="w-full h-full object-cover scale-125" />
            ) : (
              <User className="w-16 h-16" style={{ color: currentTemplate?.color }} />
            )}
          </div>
          
          {/* Orbiting particles */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                width: `${4 + (i % 3) * 2}px`,
                height: `${4 + (i % 3) * 2}px`,
                backgroundColor: currentTemplate?.color,
                borderRadius: '50%',
                left: '50%',
                top: '50%',
                transform: `rotate(${i * 30}deg) translateX(${85 + (i % 2) * 10}px) translateY(-50%)`,
                animation: `orbitParticle ${10 + i}s linear infinite`,
                opacity: 0.4 + (i % 3) * 0.2,
                boxShadow: `0 0 ${6 + i}px ${currentTemplate?.color}`
              }}
            />
          ))}
          
          {/* Corner accent lights */}
          <div 
            className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
            style={{ 
              backgroundColor: currentTemplate?.color,
              boxShadow: `0 0 20px ${currentTemplate?.color}`,
              animation: 'twinkle 1.5s ease-in-out infinite'
            }}
          />
          <div 
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
            style={{ 
              backgroundColor: currentTemplate?.color,
              boxShadow: `0 0 20px ${currentTemplate?.color}`,
              animation: 'twinkle 1.5s ease-in-out infinite 0.75s'
            }}
          />
        </div>
        
        {/* Name with enhanced glow and animation */}
        <h3 
          className="text-4xl font-black mb-3 tracking-wide relative"
          style={{ 
            color: currentTemplate?.color,
            textShadow: `0 0 30px ${currentTemplate?.color}80, 0 0 60px ${currentTemplate?.color}40, 0 0 90px ${currentTemplate?.color}20`,
            animation: 'textGlow 3s ease-in-out infinite'
          }}
        >
          {card?.display_name || username}
          {/* Underline accent */}
          <span 
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 rounded-full"
            style={{
              width: '60%',
              background: `linear-gradient(90deg, transparent, ${currentTemplate?.color}, transparent)`,
              boxShadow: `0 0 10px ${currentTemplate?.color}`
            }}
          />
        </h3>
        
        {/* Tagline with badge style */}
        {card?.tagline && (
          <div 
            className="inline-block px-6 py-2 rounded-full mb-4"
            style={{ 
              background: `linear-gradient(135deg, ${currentTemplate?.color}20, ${currentTemplate?.color}05)`,
              border: `1px solid ${currentTemplate?.color}40`,
              boxShadow: `0 0 20px ${currentTemplate?.color}15`
            }}
          >
            <p 
              className="text-sm font-bold tracking-[0.25em] uppercase"
              style={{ color: currentTemplate?.color }}
            >
              {card.tagline}
            </p>
          </div>
        )}
        
        {/* Bio with glass effect */}
        {card?.bio && (
          <div 
            className="max-w-sm mx-auto mb-8 p-4 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${currentTemplate?.color}08, transparent)`,
              border: `1px solid ${currentTemplate?.color}15`
            }}
          >
            <p className="text-gray-300 text-sm leading-relaxed">{card.bio}</p>
          </div>
        )}

        {/* Decorative divider with icons */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div 
            className="w-20 h-px"
            style={{ 
              background: `linear-gradient(90deg, transparent, ${currentTemplate?.color}60)`,
            }}
          />
          <div 
            className="w-3 h-3 rotate-45"
            style={{ 
              backgroundColor: currentTemplate?.color,
              boxShadow: `0 0 15px ${currentTemplate?.color}`
            }}
          />
          <div 
            className="w-20 h-px"
            style={{ 
              background: `linear-gradient(90deg, ${currentTemplate?.color}60, transparent)`,
            }}
          />
        </div>

        {/* FLOATING SOCIAL ICONS - Stunning Metaverse Style */}
        {card?.social_links?.length > 0 && (
          <div className="relative py-8 mb-6">
            {/* Orbiting container */}
            <div className="relative w-48 h-48 mx-auto">
              {/* Central glow */}
              <div 
                className="absolute inset-0 rounded-full animate-pulse"
                style={{
                  background: `radial-gradient(circle, ${currentTemplate?.color}20 0%, transparent 70%)`
                }}
              />
              
              {/* Orbit rings */}
              <div 
                className="absolute inset-4 rounded-full border opacity-30"
                style={{ 
                  borderColor: currentTemplate?.color,
                  animation: 'spin 20s linear infinite'
                }}
              />
              <div 
                className="absolute inset-8 rounded-full border opacity-20"
                style={{ 
                  borderColor: currentTemplate?.color,
                  animation: 'spin 15s linear infinite reverse'
                }}
              />
              
              {/* Floating social icons */}
              {card.social_links.slice(0, 6).map((link, idx) => {
                const Icon = getSocialIcon(link.platform);
                const socialColor = getSocialColor(link.platform);
                const totalIcons = Math.min(card.social_links.length, 6);
                const angle = (idx / totalIcons) * 360;
                const radius = 75;
                const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
                const y = Math.sin((angle - 90) * Math.PI / 180) * radius;
                
                return (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 hover:scale-125 hover:z-20 group"
                    style={{ 
                      left: `calc(50% + ${x}px - 28px)`,
                      top: `calc(50% + ${y}px - 28px)`,
                      backgroundColor: `${socialColor}15`,
                      border: `2px solid ${socialColor}60`,
                      boxShadow: `0 0 25px ${socialColor}40, 0 0 50px ${socialColor}20, inset 0 0 15px ${socialColor}15`,
                      animation: `floatIcon ${3 + idx * 0.5}s ease-in-out infinite`,
                      animationDelay: `${idx * 0.3}s`
                    }}
                  >
                    {/* Glow ring on hover */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{
                        boxShadow: `0 0 30px ${socialColor}, 0 0 60px ${socialColor}50`
                      }}
                    />
                    {/* Icon */}
                    <Icon 
                      className="w-7 h-7 relative z-10 transition-transform group-hover:scale-110" 
                      style={{ 
                        color: socialColor,
                        filter: `drop-shadow(0 0 8px ${socialColor})`
                      }} 
                    />
                    {/* Particle trail */}
                    <div 
                      className="absolute w-2 h-2 rounded-full opacity-60"
                      style={{
                        backgroundColor: socialColor,
                        animation: 'particleTrail 2s ease-out infinite',
                        animationDelay: `${idx * 0.2}s`
                      }}
                    />
                  </a>
                );
              })}
              
              {/* Center icon - Connect */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${currentTemplate?.color}30, ${currentTemplate?.color}10)`,
                  border: `2px solid ${currentTemplate?.color}50`,
                  boxShadow: `0 0 30px ${currentTemplate?.color}40`
                }}
              >
                <span className="text-2xl">&#128279;</span>
              </div>
            </div>
            
            {/* Connect label */}
            <p 
              className="text-center text-sm font-bold tracking-widest uppercase mt-4"
              style={{ 
                color: currentTemplate?.color,
                textShadow: `0 0 20px ${currentTemplate?.color}60`
              }}
            >
              Connect With Me
            </p>
          </div>
        )}

        {/* Mobile Number - Neon Button */}
        {card?.show_mobile && card?.mobile_number && (
          <a 
            href={`tel:${card.mobile_number.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105"
            style={{ 
              background: `linear-gradient(135deg, ${currentTemplate?.color}30, ${currentTemplate?.color}10)`,
              border: `2px solid ${currentTemplate?.color}60`,
              color: currentTemplate?.color,
              boxShadow: `0 0 20px ${currentTemplate?.color}30`
            }}
          >
            <Phone className="w-5 h-5" />
            {card.mobile_number}
          </a>
        )}

        {/* Contact Buttons Row */}
        <div className="flex justify-center gap-4 mt-4 flex-wrap">
          {card?.show_email && card?.email && (
            <a
              href={`mailto:${card.email}`}
              className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all hover:scale-110"
              style={{ 
                backgroundColor: `${currentTemplate?.color}15`,
                border: `2px solid ${currentTemplate?.color}40`,
                boxShadow: `0 0 15px ${currentTemplate?.color}20`
              }}
            >
              <Mail className="w-6 h-6" style={{ color: currentTemplate?.color }} />
            </a>
          )}
          {card?.show_phone && card?.phone && (
            <a
              href={`tel:${card.phone}`}
              className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all hover:scale-110"
              style={{ 
                backgroundColor: `${currentTemplate?.color}15`,
                border: `2px solid ${currentTemplate?.color}40`,
                boxShadow: `0 0 15px ${currentTemplate?.color}20`
              }}
            >
              <Phone className="w-6 h-6" style={{ color: currentTemplate?.color }} />
            </a>
          )}
          {card?.show_whatsapp && card?.whatsapp_number && (
            <a
              href={`https://wa.me/${card.whatsapp_number.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all hover:scale-110"
              style={{ 
                backgroundColor: '#25D36620',
                border: '2px solid #25D36660',
                boxShadow: '0 0 15px #25D36630'
              }}
            >
              <FaWhatsapp className="w-6 h-6 text-green-500" />
            </a>
          )}
        </div>

        {/* Custom Links - Neon Buttons */}
        {card?.custom_links?.length > 0 && (
          <div className="mt-6 space-y-3 max-w-xs mx-auto">
            {card.custom_links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-5 py-4 rounded-xl text-center font-bold transition-all hover:scale-[1.02] hover:-translate-y-1"
                style={{ 
                  background: `linear-gradient(135deg, ${currentTemplate?.color}20, transparent)`,
                  border: `1px solid ${currentTemplate?.color}40`,
                  color: currentTemplate?.color,
                  boxShadow: `0 4px 20px ${currentTemplate?.color}20`
                }}
              >
                {link.name}
              </a>
            ))}
          </div>
        )}

        {/* Referral Code - Futuristic Badge */}
        {card?.referral_code && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <p 
              className="text-xs mb-4 tracking-[0.3em] uppercase font-bold"
              style={{ color: `${currentTemplate?.color}60` }}
            >
              Join My Network
            </p>
            <div 
              className="inline-block px-10 py-5 rounded-3xl relative overflow-hidden"
              style={{ 
                background: `linear-gradient(135deg, ${currentTemplate?.color}15, ${currentTemplate?.color}05)`,
                border: `2px solid ${currentTemplate?.color}40`,
                boxShadow: `0 0 40px ${currentTemplate?.color}20, inset 0 0 30px ${currentTemplate?.color}10`
              }}
            >
              {/* Animated shine effect */}
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  background: `linear-gradient(45deg, transparent 40%, ${currentTemplate?.color}40 50%, transparent 60%)`,
                  backgroundSize: '200% 200%',
                  animation: 'shine 3s ease-in-out infinite'
                }}
              />
              <p className="text-[10px] text-gray-400 mb-2 tracking-[0.2em] font-medium relative z-10">REFERRAL CODE</p>
              <p 
                className="font-mono font-black text-3xl tracking-wider relative z-10"
                style={{ 
                  color: currentTemplate?.color,
                  textShadow: `0 0 30px ${currentTemplate?.color}80, 0 0 60px ${currentTemplate?.color}40`
                }}
              >
                {card.referral_code}
              </p>
              {/* Join button */}
              <a 
                href={`/register?ref=${card.username}`}
                className="inline-block mt-3 text-xs px-6 py-2 rounded-full font-bold transition-all hover:scale-105 relative z-10"
                style={{ 
                  backgroundColor: currentTemplate?.color, 
                  color: currentTemplate?.bg,
                  boxShadow: `0 0 20px ${currentTemplate?.color}50`
                }}
              >
                Join My Team
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // LUXURY TEMPLATE RENDERING
  const renderLuxuryCard = () => (
    <div 
      ref={cardRef}
      className="rounded-3xl overflow-hidden shadow-2xl relative"
      style={{ 
        backgroundColor: currentTemplate?.bg || '#1a1000',
        minHeight: '500px'
      }}
    >
      {/* Luxury Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Shimmer overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: `linear-gradient(45deg, transparent 30%, ${currentTemplate?.color}30 50%, transparent 70%)`,
            backgroundSize: '400% 400%',
            animation: 'shimmer 8s ease-in-out infinite'
          }}
        />
        
        {/* Subtle pattern for luxury feel */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, ${currentTemplate?.color} 0, ${currentTemplate?.color} 1px, transparent 0, transparent 50%)`,
            backgroundSize: '20px 20px'
          }}
        />
        
        {/* Diamond sparkles for diamond template */}
        {templateStyle === 'diamond' && (
          [...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background: `linear-gradient(45deg, ${currentTemplate?.color}, transparent)`,
                transform: 'rotate(45deg)',
                animation: `diamondSparkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))
        )}

        {/* Carbon fiber pattern */}
        {templateStyle === 'carbon' && (
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                linear-gradient(45deg, #1a1a1a 25%, transparent 25%),
                linear-gradient(-45deg, #1a1a1a 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #1a1a1a 75%),
                linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)
              `,
              backgroundSize: '10px 10px',
              backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
            }}
          />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 px-8 py-10 text-center">
        {/* Elegant Profile Frame */}
        <div className="relative w-40 h-40 mx-auto mb-8">
          {/* Outer decorative ring */}
          <div 
            className="absolute -inset-4 rounded-full"
            style={{ 
              border: `1px solid ${currentTemplate?.color}30`,
              animation: 'luxuryFloat 4s ease-in-out infinite'
            }}
          />
          
          {/* Golden/luxury ring */}
          <div 
            className="absolute -inset-1 rounded-full"
            style={{ 
              background: `linear-gradient(135deg, ${currentTemplate?.color}60, ${currentTemplate?.color}20, ${currentTemplate?.color}60)`,
              animation: 'spin 20s linear infinite'
            }}
          />
          
          {/* Profile container */}
          <div 
            className="absolute inset-1 rounded-full overflow-hidden border-2"
            style={{ 
              borderColor: currentTemplate?.color,
              backgroundColor: currentTemplate?.bg,
              boxShadow: `0 0 30px ${currentTemplate?.color}30, inset 0 0 20px ${currentTemplate?.color}10`
            }}
          >
            {card?.profile_image ? (
              <img src={getImageUrl(card.profile_image)} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-16 h-16" style={{ color: currentTemplate?.color }} />
              </div>
            )}
          </div>
          
          {/* Crown icon for luxury */}
          <div 
            className="absolute -top-2 left-1/2 -translate-x-1/2 text-2xl"
            style={{ filter: `drop-shadow(0 0 10px ${currentTemplate?.color})` }}
          >
            &#128081;
          </div>
        </div>

        {/* Name with elegant styling */}
        <h3 
          className="text-3xl font-bold mb-2"
          style={{ 
            color: currentTemplate?.color,
            textShadow: `0 0 20px ${currentTemplate?.color}50`,
            fontFamily: 'Georgia, serif'
          }}
        >
          {card?.display_name || username}
        </h3>
        
        {/* Decorative line */}
        <div 
          className="w-24 h-0.5 mx-auto mb-4"
          style={{ 
            background: `linear-gradient(90deg, transparent, ${currentTemplate?.color}, transparent)`,
            boxShadow: `0 0 10px ${currentTemplate?.color}50`
          }}
        />

        {card?.tagline && (
          <p 
            className="text-sm mb-4 tracking-wider uppercase"
            style={{ color: `${currentTemplate?.color}90` }}
          >
            {card.tagline}
          </p>
        )}

        {card?.bio && (
          <p className="text-gray-400 text-sm mb-6 max-w-xs mx-auto italic">
            {card.bio}
          </p>
        )}

        {/* Elegant Social Links */}
        {card?.social_links?.length > 0 && (
          <div className="flex justify-center gap-4 mb-6 flex-wrap">
            {card.social_links.map((link, idx) => {
              const Icon = getSocialIcon(link.platform);
              const socialColor = getSocialColor(link.platform);
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ 
                    border: `1px solid ${currentTemplate?.color}40`,
                    backgroundColor: `${socialColor}10`,
                    boxShadow: `0 0 15px ${socialColor}20`
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: socialColor }} />
                </a>
              );
            })}
          </div>
        )}

        {/* Contact Info */}
        <div className="flex justify-center gap-4 mb-6 flex-wrap">
          {card?.show_mobile && card?.mobile_number && (
            <a 
              href={`tel:${card.mobile_number.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-all hover:scale-105"
              style={{ 
                border: `1px solid ${currentTemplate?.color}40`,
                color: currentTemplate?.color
              }}
            >
              <Phone className="w-4 h-4" />
              {card.mobile_number}
            </a>
          )}
          {card?.show_email && card?.email && (
            <a
              href={`mailto:${card.email}`}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ 
                border: `1px solid ${currentTemplate?.color}40`,
                backgroundColor: `${currentTemplate?.color}10`
              }}
            >
              <Mail className="w-5 h-5" style={{ color: currentTemplate?.color }} />
            </a>
          )}
          {card?.show_whatsapp && card?.whatsapp_number && (
            <a
              href={`https://wa.me/${card.whatsapp_number.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ 
                border: '1px solid #25D36640',
                backgroundColor: '#25D36610'
              }}
            >
              <FaWhatsapp className="w-5 h-5 text-green-500" />
            </a>
          )}
        </div>

        {/* Custom Links */}
        {card?.custom_links?.length > 0 && (
          <div className="space-y-3 mb-6 max-w-xs mx-auto">
            {card.custom_links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-6 py-3 rounded-xl text-center font-medium transition-all hover:scale-[1.02]"
                style={{ 
                  border: `1px solid ${currentTemplate?.color}30`,
                  color: currentTemplate?.color,
                  backgroundColor: `${currentTemplate?.color}08`
                }}
              >
                {link.name}
              </a>
            ))}
          </div>
        )}

        {/* Referral Code */}
        {card?.referral_code && (
          <div 
            className="mt-6 pt-6 border-t"
            style={{ borderColor: `${currentTemplate?.color}20` }}
          >
            <p className="text-xs text-gray-500 mb-2 tracking-wider">EXCLUSIVE CODE</p>
            <p 
              className="font-mono text-2xl font-bold mb-3"
              style={{ 
                color: currentTemplate?.color,
                textShadow: `0 0 15px ${currentTemplate?.color}50`
              }}
            >
              {card.referral_code}
            </p>
            <a 
              href={`/register?ref=${card.username}`}
              className="inline-block text-sm px-6 py-2 rounded-full font-medium transition-all hover:scale-105"
              style={{ 
                backgroundColor: currentTemplate?.color, 
                color: currentTemplate?.bg
              }}
            >
              Join My Team
            </a>
          </div>
        )}
      </div>
    </div>
  );

  // CLASSIC TEMPLATE RENDERING (fallback)
  const renderClassicCard = () => (
    <div ref={cardRef} className="rounded-3xl overflow-hidden shadow-2xl" style={{ backgroundColor: bgColor }}>
      {/* Cover Image */}
      <div 
        className="h-40 relative"
        style={{ 
          background: card?.cover_image 
            ? `url(${getImageUrl(card.cover_image)}) center/cover` 
            : `linear-gradient(135deg, ${primaryColor}40, ${primaryColor}10)`
        }}
      />

      {/* Profile Section */}
      <div className="px-6 pb-8 -mt-16 text-center relative">
        {/* Profile Image */}
        <div 
          className="w-32 h-32 rounded-full mx-auto mb-4 border-4 bg-gray-800 flex items-center justify-center overflow-hidden shadow-xl"
          style={{ borderColor: primaryColor }}
        >
          {card?.profile_image ? (
            <img src={getImageUrl(card.profile_image)} alt={card.display_name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-16 h-16 text-gray-500" />
          )}
        </div>

        {/* Name & Tagline */}
        <h1 className="text-3xl font-bold mb-1" style={{ color: primaryColor }}>
          {card?.display_name || username}
        </h1>
        {card?.tagline && (
          <p className="text-gray-400 mb-4">{card.tagline}</p>
        )}
        {card?.bio && (
          <p className="text-gray-300 text-sm mb-6 max-w-xs mx-auto">{card.bio}</p>
        )}

        {/* Contact Info */}
        <div className="flex justify-center gap-4 mb-6">
          {card?.show_email && card?.email && (
            <a
              href={`mailto:${card.email}`}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Mail className="w-6 h-6" style={{ color: primaryColor }} />
            </a>
          )}
          {card?.show_phone && card?.phone && (
            <a
              href={`tel:${card.phone}`}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Phone className="w-6 h-6" style={{ color: primaryColor }} />
            </a>
          )}
          {card?.show_mobile && card?.mobile_number && (
            <a
              href={`tel:${card.mobile_number.replace(/\s/g, '')}`}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Phone className="w-6 h-6" style={{ color: primaryColor }} />
            </a>
          )}
          {card?.show_whatsapp && card?.whatsapp_number && (
            <a
              href={`https://wa.me/${card.whatsapp_number.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: '#25D36620' }}
            >
              <FaWhatsapp className="w-6 h-6 text-green-500" />
            </a>
          )}
        </div>

        {/* Social Links */}
        {card?.social_links?.length > 0 && (
          <div className="flex justify-center gap-3 mb-6 flex-wrap">
            {card.social_links.map((link, idx) => {
              const Icon = getSocialIcon(link.platform);
              const color = getSocialColor(link.platform);
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ backgroundColor: `${color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color }} />
                </a>
              );
            })}
          </div>
        )}

        {/* Custom Links */}
        {card?.custom_links?.length > 0 && (
          <div className="space-y-3 mb-6">
            {card.custom_links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-6 py-3 rounded-xl font-semibold text-center transition-all hover:scale-[1.02]"
                style={{ 
                  backgroundColor: `${primaryColor}20`,
                  color: primaryColor
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  {link.name}
                  <ExternalLink className="w-4 h-4" />
                </span>
              </a>
            ))}
          </div>
        )}

        {/* Referral Code */}
        {card?.referral_code && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-xs text-gray-500 mb-1">Referral Code</p>
            <p className="font-mono text-xl font-bold" style={{ color: primaryColor }}>
              {card.referral_code}
            </p>
            <a 
              href={`/register?ref=${card.username}`}
              className="inline-block mt-3 text-sm px-6 py-2 rounded-lg font-semibold transition-all hover:scale-105"
              style={{ backgroundColor: primaryColor, color: bgColor }}
            >
              Join My Team
            </a>
          </div>
        )}
      </div>
    </div>
  );

  // Generate OG image URL - use actual profile image
  // Use window.location.origin for social sharing - ensures correct domain
  const baseUrl = window.location.origin;
  const backendUrl = baseUrl; // For image URLs and share URLs
  const getOgImageUrl = () => {
    if (card?.profile_image) {
      const img = card.profile_image;
      if (img.startsWith('http')) return img;
      return `${backendUrl}${img}`;
    }
    return `${backendUrl}/static/og-default.png`;
  };
  const ogImageUrl = getOgImageUrl();
  // IMPORTANT: Use /api/card/{username} for sharing - this serves proper OG tags
  const shareUrl = `${baseUrl}/api/card/${username}`; // For social media sharing with OG tags
  const pageTitle = card?.display_name ? `${card.display_name} - Digital Card` : `${username}'s Digital Card`;
  const pageDescription = card?.tagline || card?.bio || `View ${card?.display_name || username}'s digital visiting card`;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: bgColor }}>
      {/* SEO Meta Tags for Social Sharing */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={cardUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${card?.display_name || username}'s Digital Visiting Card`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={cardUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        
        {/* Additional Meta */}
        <meta property="profile:username" content={username} />
        <link rel="canonical" href={cardUrl} />
      </Helmet>
      
      <MetaverseStyles />
      <div className="max-w-md mx-auto">
        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={() => setShowQR(true)}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            title="Show QR Code"
          >
            <QrCode className="w-5 h-5" />
          </button>
          <button
            onClick={downloadCard}
            disabled={downloading}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            title="Download Card"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowSharePopup(true)}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            title="Share Card"
            data-testid="dvc-share-btn"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* QR Code Modal */}
        {showQR && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
            <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-white mb-4">Scan to View Card</h3>
              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCodeSVG value={cardUrl} size={200} level="H" />
              </div>
              <p className="text-sm text-gray-400 mb-4 break-all">{cardUrl}</p>
              <button 
                onClick={() => setShowQR(false)} 
                className="px-6 py-2 rounded-xl text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Render appropriate template */}
        {templateStyle === 'business-glass' || templateStyle === 'business-navy-gold' || templateStyle === 'business-executive' || templateStyle === 'business-modern' || templateStyle === 'corporate-blue' || templateStyle === 'corporate-teal' ? (
          // SARAH JENSEN STYLE - Business Glass with Left Photo Frame
          <div 
            ref={cardRef}
            className="rounded-3xl overflow-hidden relative"
            style={{
              background: `linear-gradient(135deg, #0a1628 0%, #1a2744 50%, #0d1f3a 100%)`,
              boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5), 0 0 60px ${currentTemplate?.color}20`
            }}
          >
            {/* Glass overlay */}
            <div className="absolute inset-0 backdrop-blur-sm bg-white/5"></div>
            
            <div className="relative p-4 sm:p-6">
              {/* Mobile: Stack layout, Desktop: Side-by-side */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Profile Photo Frame - Centered on mobile */}
                <div className="w-28 sm:w-40 flex-shrink-0 mx-auto sm:mx-0">
                  <div 
                    className="aspect-[3/4] rounded-2xl overflow-hidden border-3 sm:border-4"
                    style={{ 
                      borderColor: currentTemplate?.color,
                      boxShadow: `0 0 20px ${currentTemplate?.color}40, inset 0 0 15px ${currentTemplate?.color}20`
                    }}
                  >
                    {card?.profile_image ? (
                      <img src={getImageUrl(card.profile_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <User className="w-12 sm:w-16 h-12 sm:h-16 text-gray-600" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Content - Centered on mobile */}
                <div className="flex-1 flex flex-col justify-between text-center sm:text-left">
                  {/* Name & QR - Stack on mobile */}
                  <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
                    <div>
                      <h1 
                        className="text-xl sm:text-3xl font-black uppercase tracking-wider"
                        style={{ color: currentTemplate?.color }}
                      >
                        {card?.display_name?.split(' ')[0] || username}
                      </h1>
                      <h2 className="text-xl sm:text-3xl font-black text-white uppercase tracking-wider">
                        {card?.display_name?.split(' ').slice(1).join(' ') || ''}
                      </h2>
                    </div>
                    {/* QR Code - Hidden on very small screens */}
                    <div 
                      className="hidden xs:block p-1.5 sm:p-2 rounded-lg"
                      style={{ 
                        border: `2px solid ${currentTemplate?.color}`,
                        background: 'white'
                      }}
                    >
                      <QRCodeSVG value={cardUrl} size={50} level="L" />
                      <p className="text-[7px] sm:text-[8px] text-center mt-1 text-gray-600 font-bold">ELEVATE YOUR BRAND</p>
                    </div>
                  </div>
                  
                  {/* Title */}
                  <p 
                    className="text-xs sm:text-sm font-bold uppercase tracking-widest mt-2"
                    style={{ color: currentTemplate?.color }}
                  >
                    {card?.tagline || 'CREATIVE DIRECTOR | BRAND STRATEGIST'}
                  </p>
                  
                  {/* Bio */}
                  <p className="text-gray-400 text-xs sm:text-sm mt-2 leading-relaxed line-clamp-3">
                    {card?.bio || 'Crafting exceptional visual identities and innovative brand experiences.'}
                  </p>
                  
                  {/* Social Icons Row - Wrap on mobile */}
                  {card?.social_links?.length > 0 && (
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-4 mt-3 sm:mt-4">
                      {card.social_links.slice(0, 5).map((link, idx) => {
                        const Icon = getSocialIcon(link.platform);
                        const color = getSocialColor(link.platform);
                        return (
                          <a 
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center gap-0.5 sm:gap-1"
                          >
                            <div 
                              className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: `${color}30` }}
                            >
                              <Icon className="w-4 sm:w-5 h-4 sm:h-5" style={{ color }} />
                            </div>
                            <span className="text-[7px] sm:text-[8px] text-gray-500 hidden sm:block">@{link.username || link.platform}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Email & Location */}
                  <div className="mt-3 sm:mt-4 space-y-1">
                    {card?.show_email && card?.email && (
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-gray-400 text-xs sm:text-sm">
                        <Mail className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: currentTemplate?.color }} />
                        <span className="truncate max-w-[200px]">{card.email}</span>
                      </div>
                    )}
                    {card?.tagline && (
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-gray-400 text-xs sm:text-sm">
                        <MapPin className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: currentTemplate?.color }} />
                        <span className="truncate max-w-[200px]">{card.address || 'Location'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Call Now Button */}
              {card?.show_phone && card?.phone && (
                <a
                  href={`tel:${card.phone}`}
                  className="mt-4 sm:mt-6 block w-full py-3 sm:py-4 rounded-xl sm:rounded-2xl text-center font-black text-base sm:text-xl text-white uppercase tracking-wider"
                  style={{
                    background: `linear-gradient(90deg, ${currentTemplate?.color}, #FF8C00, ${currentTemplate?.color})`,
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 2s infinite',
                    boxShadow: `0 10px 30px ${currentTemplate?.color}40`
                  }}
                >
                  <Phone className="w-5 sm:w-6 h-5 sm:h-6 inline mr-2" />
                  CALL NOW
                  <span className="block text-[10px] sm:text-xs font-normal mt-1 opacity-80">Tap to Connect Directly</span>
                </a>
              )}
            </div>
          </div>
        ) : templateStyle === 'neon-ring' || templateStyle === 'neon-ring-cyan' || templateStyle === 'neon-ring-purple' || templateStyle === 'neon-ring-green' || templateStyle === 'neon-ring-pink' || templateStyle === 'neon-ring-orange' ? (
          // ALEX RIVERS STYLE - Neon Ring Profile with Grid Social Links
          <div 
            ref={cardRef}
            className="rounded-3xl overflow-hidden relative"
            style={{
              background: `linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 50%, #0f0f1f 100%)`,
              boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5)`
            }}
          >
            <div className="p-5 sm:p-8 text-center">
              {/* Profile Photo with Neon Ring - Smaller on mobile */}
              <div className="relative w-28 sm:w-36 h-28 sm:h-36 mx-auto mb-4 sm:mb-6">
                <div 
                  className="absolute inset-0 rounded-full animate-pulse"
                  style={{
                    background: `conic-gradient(from 0deg, ${currentTemplate?.color}, #A855F7, #3B82F6, ${currentTemplate?.color})`,
                    filter: `blur(6px)`,
                    opacity: 0.8
                  }}
                />
                <div 
                  className="absolute inset-1 rounded-full"
                  style={{
                    background: `conic-gradient(from 0deg, ${currentTemplate?.color}, #A855F7, #3B82F6, ${currentTemplate?.color})`,
                    padding: '3px'
                  }}
                >
                  <div className="w-full h-full rounded-full overflow-hidden bg-gray-900">
                    {card?.profile_image ? (
                      <img src={getImageUrl(card.profile_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 sm:w-16 h-12 sm:h-16 text-gray-600" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Name */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">
                {card?.display_name || username}
              </h1>
              <p 
                className="text-xs sm:text-sm font-medium mb-2 px-2"
                style={{ color: currentTemplate?.color }}
              >
                {card?.tagline || 'Digital Strategist | Content Creator'}
              </p>
              <p className="text-gray-400 text-xs sm:text-sm max-w-xs mx-auto line-clamp-2">
                {card?.bio || 'Driving digital growth through data-driven creativity.'}
              </p>
              
              {/* Social Links Grid - Single column on very small screens */}
              {card?.social_links?.length > 0 && (
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3 mt-4 sm:mt-6">
                  {card.social_links.slice(0, 6).map((link, idx) => {
                    const Icon = getSocialIcon(link.platform);
                    const color = getSocialColor(link.platform);
                    return (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg sm:rounded-xl transition-all hover:scale-[1.02]"
                        style={{
                          background: `linear-gradient(135deg, ${color}15, ${color}05)`,
                          border: `1px solid ${color}30`
                        }}
                      >
                        <Icon className="w-4 sm:w-5 h-4 sm:h-5 flex-shrink-0" style={{ color }} />
                        <div className="text-left min-w-0">
                          <p className="text-white text-xs sm:text-sm font-medium capitalize truncate">{link.platform}</p>
                          <p className="text-gray-500 text-[10px] sm:text-xs truncate">@{link.username || link.platform}</p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
              
              {/* Stats Row - Scrollable on mobile */}
              <div className="flex justify-start sm:justify-center gap-4 sm:gap-6 mt-4 sm:mt-6 py-3 sm:py-4 border-t border-b border-white/10 overflow-x-auto">
                <div className="text-center flex-shrink-0">
                  <p className="text-white font-bold text-sm sm:text-base">10+</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs">EXPERIENCE</p>
                </div>
                <div className="text-center flex-shrink-0">
                  <p className="text-white font-bold text-sm sm:text-base">Featured</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs">PORTFOLIO</p>
                </div>
                <div className="text-center flex-shrink-0">
                  <p className="text-white font-bold text-sm sm:text-base">SEO</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs">SERVICES</p>
                </div>
                <div className="text-center flex-shrink-0">
                  <p className="text-white font-bold text-sm sm:text-base">Top</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs">AWARDS</p>
                </div>
              </div>
              
              {/* Connect Button */}
              <a
                href={card?.show_phone && card?.phone ? `tel:${card.phone}` : '#'}
                className="mt-4 sm:mt-6 block w-full py-3 sm:py-4 rounded-xl text-center font-bold text-white uppercase tracking-wider text-sm sm:text-base transition-all hover:scale-[1.02]"
                style={{
                  background: `linear-gradient(90deg, ${currentTemplate?.color}80, #A855F780)`,
                  boxShadow: `0 10px 30px ${currentTemplate?.color}30`
                }}
              >
                CONNECT
              </a>
              <button className="mt-2 sm:mt-3 text-gray-500 text-xs sm:text-sm hover:text-white transition-colors">
                SAVE CONTACT
              </button>
            </div>
          </div>
        ) : templateStyle === 'creative-studio' || templateStyle === 'creative-studio-purple' || templateStyle === 'creative-vision-blue' || templateStyle === 'designer-pro' || templateStyle === 'artist-palette' || templateStyle === 'tech-innovator' ? (
          // MAYA SHARMA STYLE - Creative Studio with Abstract Background
          <div 
            ref={cardRef}
            className="rounded-3xl overflow-hidden relative"
            style={{
              background: `linear-gradient(135deg, #0f0a2a 0%, #1a1040 50%, #0d0820 100%)`,
              boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5), 0 0 80px ${currentTemplate?.color}15`
            }}
          >
            {/* Abstract shapes background - Smaller on mobile */}
            <div className="absolute inset-0 overflow-hidden opacity-30">
              <div className="absolute -top-10 sm:-top-20 -right-10 sm:-right-20 w-32 sm:w-60 h-32 sm:h-60 rounded-full" style={{ background: `radial-gradient(circle, ${currentTemplate?.color}40, transparent)` }} />
              <div className="absolute top-20 sm:top-40 -left-10 sm:-left-20 w-24 sm:w-40 h-24 sm:h-40 rounded-full" style={{ background: `radial-gradient(circle, #A855F740, transparent)` }} />
              <div className="absolute bottom-10 sm:bottom-20 right-10 sm:right-20 w-20 sm:w-32 h-20 sm:h-32 rounded-full" style={{ background: `radial-gradient(circle, #3B82F640, transparent)` }} />
            </div>
            
            <div className="relative p-5 sm:p-8 text-center">
              {/* Logo Badge */}
              <div className="absolute top-3 sm:top-4 left-3 sm:left-4 flex items-center gap-1.5 sm:gap-2">
                <div 
                  className="w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center"
                  style={{ background: currentTemplate?.color }}
                >
                  <Star className="w-3 sm:w-4 h-3 sm:h-4 text-white" />
                </div>
                <span className="text-white font-bold text-xs sm:text-sm hidden xs:block">CREATIVE VISIONS</span>
              </div>
              
              {/* Profile Photo with Glow Frame - Smaller on mobile */}
              <div className="relative w-32 sm:w-44 h-40 sm:h-52 mx-auto mt-8 sm:mt-10 mb-4 sm:mb-6">
                <div 
                  className="absolute inset-0 rounded-xl sm:rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, ${currentTemplate?.color}, #A855F7, #3B82F6)`,
                    filter: 'blur(10px)',
                    opacity: 0.6
                  }}
                />
                <div 
                  className="absolute inset-0 rounded-xl sm:rounded-2xl p-0.5 sm:p-1"
                  style={{
                    background: `linear-gradient(135deg, ${currentTemplate?.color}, #A855F7, #3B82F6)`
                  }}
                >
                  <div className="w-full h-full rounded-lg sm:rounded-xl overflow-hidden bg-gray-900">
                    {card?.profile_image ? (
                      <img src={getImageUrl(card.profile_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-14 sm:w-20 h-14 sm:h-20 text-gray-600" />
                      </div>
                    )}
                  </div>
                </div>
                {/* Star badge - Smaller on mobile */}
                <div 
                  className="absolute -top-2 sm:-top-3 right-2 sm:right-4 w-7 sm:w-10 h-7 sm:h-10 rounded-full flex items-center justify-center"
                  style={{ background: currentTemplate?.color }}
                >
                  <span className="text-white font-bold text-xs sm:text-sm">M</span>
                </div>
              </div>
              
              {/* Name */}
              <h1 className="text-xl sm:text-2xl font-black text-white uppercase tracking-wider">
                {card?.display_name || username}
              </h1>
              <p 
                className="text-xs sm:text-sm font-bold uppercase tracking-widest mt-1 px-2"
                style={{ color: currentTemplate?.color }}
              >
                {card?.tagline || 'DIGITAL CREATIVE DIRECTOR'}
              </p>
              <p className="text-gray-400 text-xs sm:text-sm italic mt-2 line-clamp-2 px-2">
                "{card?.bio || 'Crafting Experiences, Inspiring Connections.'}"
              </p>
              
              {/* Social Links - Responsive grid */}
              {card?.social_links?.length > 0 && (
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3 mt-4 sm:mt-6">
                  {card.social_links.slice(0, 4).map((link, idx) => {
                    const Icon = getSocialIcon(link.platform);
                    const color = getSocialColor(link.platform);
                    return (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition-all hover:scale-[1.02]"
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                      >
                        <Icon className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" style={{ color }} />
                        <span className="text-white text-xs sm:text-sm truncate">/{link.username || link.platform}</span>
                      </a>
                    );
                  })}
                </div>
              )}
              
              {/* Contact Info */}
              <p className="text-gray-400 text-xs sm:text-sm mt-3 sm:mt-4 truncate px-2">
                {card?.phone && `${card.phone} | `}{card?.email}
              </p>
              
              {/* Action Buttons */}
              <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                <button 
                  className="w-full py-2 rounded-full text-white text-xs sm:text-sm font-medium"
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  Add to Contacts
                </button>
                <a
                  href={card?.show_phone && card?.phone ? `tel:${card.phone}` : '#'}
                  className="block w-full py-3 sm:py-4 rounded-full text-center font-black text-base sm:text-xl text-white uppercase"
                  style={{
                    background: `linear-gradient(90deg, #FF6B6B, ${currentTemplate?.color}, #3B82F6)`,
                    boxShadow: `0 10px 30px rgba(255,107,107,0.3)`
                  }}
                >
                  CALL ME NOW
                  <Phone className="w-4 sm:w-5 h-4 sm:h-5 inline ml-2" />
                </a>
              </div>
            </div>
          </div>
        ) : is4KCosmic ? render4KCosmicCard() : isCybercity ? renderCybercityCard() : isMetaverse ? renderMetaverseCard() : isLuxury ? renderLuxuryCard() : renderClassicCard()}

        {/* Direct Share Buttons - Always visible at bottom */}
        <div className="mt-6 p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <p className="text-center text-xs text-gray-400 mb-3">Share this card</p>
          <div className="flex justify-center gap-3">
            {/* WhatsApp */}
            <button
              onClick={() => {
                const text = `${card?.display_name || username}'s Digital Visiting Card\n${shareUrl}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ background: '#25D366' }}
              title="Share on WhatsApp"
            >
              <FaWhatsapp className="w-5 h-5 text-white" />
            </button>
            
            {/* Facebook */}
            <button
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ background: '#1877F2' }}
              title="Share on Facebook"
            >
              <FaFacebook className="w-5 h-5 text-white" />
            </button>
            
            {/* Twitter */}
            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent((card?.display_name || username) + "'s Digital Card")}`, '_blank')}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ background: '#1DA1F2' }}
              title="Share on Twitter"
            >
              <FaTwitter className="w-5 h-5 text-white" />
            </button>
            
            {/* Telegram */}
            <button
              onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent((card?.display_name || username) + "'s Digital Card")}`, '_blank')}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ background: '#0088cc' }}
              title="Share on Telegram"
            >
              <FaTelegram className="w-5 h-5 text-white" />
            </button>
            
            {/* Copy Link */}
            <button
              onClick={async () => {
                await copyToClipboard(shareUrl);
                toast.success('Link copied!');
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ background: primaryColor }}
              title="Copy Link"
            >
              <ExternalLink className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-xs">
            Powered by <a href="/" className="hover:underline" style={{ color: primaryColor }}>ANANT WEB PORTAL</a>
          </p>
        </div>
      </div>
      
      {/* Share Popup */}
      <SharePopup 
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        shareUrl={shareUrl}
        title={card?.display_name ? `${card.display_name} - Digital Visiting Card` : `${username}'s Digital Card`}
        description={card?.tagline || card?.bio || 'Digital Visiting Card'}
        itemType="dvc"
        itemId={username}
      />
    </div>
  );
};

export default PublicCard;
